import isEmpty from 'lodash/isEmpty';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellIcon } from '~/components/UpsellIcons';
import {
  useHasAutomaticReconciliation,
  useHasCashflow,
  useHasCreditLines,
  useHasF24Management,
  useHasHideBalances,
  useHasPayments,
  useHasTreasury,
  useIsBankAccountLeadSource,
  useIsEntrepeneur,
} from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import { useUserBankAccountPermissions } from '~/hooks/useUserBankAccountPermissions';
import BankIcon from '~/icons/icon-building.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useAllAccountsQuery } from '~/services/account';
import { useBankingConsents } from '~/services/consents';
import { usePaymentsMetadata } from '~/services/payment';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { NewFeatureName, useShowNewFeatureBadge } from '~/state/newFeatureBadge';
import { UserRole } from '~/types/company';
import { PaymentStatus } from '~/types/payment';
import { SubmenuSection } from '~/types/sidebar';
import { isConsentNonAuthorised, isConsentVisibleToUser, isSwanConsent, isVisibleSwanConsent } from '~/utils/consents';
import NavigationSidebarItem from '../NavigationSidebarItem';

interface Props {
  disabled?: boolean;
}

const BanksItem: React.FC<Props> = ({ disabled }) => {
  const { t } = useTranslation('sidebar');
  const hasAccess = useHasTreasury();
  const hasCashflow = useHasCashflow();
  const hasAutomaticReconciliation = useHasAutomaticReconciliation();
  const hasPayments = useHasPayments();
  const hasCreditLines = useHasCreditLines();
  const hasTreasury = useHasTreasury([UserRole.ENTREPRENEUR]);
  const hasF24Management = useHasF24Management();
  const hasHideBalances = useHasHideBalances();
  const isSubmenuOpen = useIsSubmenuOpen('banks');
  const openSubmenu = useSetSubmenu('banks');
  const consentsQuery = useBankingConsents();
  const swanOnboardingInProgress = consentsQuery.data?.some(isVisibleSwanConsent);
  const unauthorizedConsents = useMemo(
    () =>
      (consentsQuery.data ?? []).filter(
        (c) => isConsentVisibleToUser(c) && isConsentNonAuthorised(c) && !isSwanConsent(c),
      ).length,
    [consentsQuery.data],
  );
  const accountsQuery = useAllAccountsQuery();
  const hasZeroAccounts = accountsQuery.data && isEmpty(accountsQuery.data) && !swanOnboardingInProgress;
  const { data: timeoutData } = usePaymentsMetadata({ status: [PaymentStatus.Timeout] });
  const timeoutTotalCount = timeoutData?.total ?? 0;
  const { isSibillAccountEnabled } = useUserBankAccountPermissions();
  const isEntrepreneur = useIsEntrepeneur();
  const isBankAccountLeadSource = useIsBankAccountLeadSource();
  const showNewFeatureBadge = useShowNewFeatureBadge(NewFeatureName.F24);

  const showAutomaticReconciliation = !isBankAccountLeadSource && (isEntrepreneur || hasAutomaticReconciliation);
  const showCreditLines = hasCreditLines && hasTreasury;
  const showRules = !isBankAccountLeadSource && (isEntrepreneur || (hasAccess && hasCashflow));

  const showRulesUpsellIcon = !hasCashflow && hasAccess;

  const accountsNotification = unauthorizedConsents + (hasZeroAccounts ? 1 : 0);
  const notificationBadgeCount = accountsNotification + timeoutTotalCount;

  const isOnlySwan = !hasAccess && !isEntrepreneur && isSibillAccountEnabled;

  const landingPage =
    hasZeroAccounts || timeoutTotalCount === 0 ? RouteConfig.Movements.buildLink() : RouteConfig.Payments.buildLink();

  const sections: SubmenuSection[] = useMemo(() => {
    const overviewSubsection: SubmenuSection['items'] = [];

    if (!isOnlySwan) {
      overviewSubsection.push({
        label: t('accounts.submenu.transactions'),
        href: `${RouteConfig.Transactions.buildLink()}/movements`,
      });
    }

    if (hasPayments) {
      overviewSubsection.push({
        label: t('accounts.submenu.payments'),
        href: RouteConfig.Payments.buildLink(),
        notificationCount: timeoutTotalCount,
      });
    }

    if (hasF24Management) {
      overviewSubsection.push({
        label: t('accounts.submenu.f24'),
        href: RouteConfig.F24.buildLink(),
        newFeatureBadge: showNewFeatureBadge,
      });
    }

    if (showAutomaticReconciliation) {
      overviewSubsection.push({
        label: t('accounts.submenu.automatic_reconciliations'),
        href: RouteConfig.Reconciliations.buildLink(),
        icon: !hasAutomaticReconciliation ? UpsellIcon.VariantAnimated : undefined,
      });
    }

    const accountAndCardSubsection: SubmenuSection['items'] = [];

    if (!hasHideBalances) {
      const notificationTooltip =
        unauthorizedConsents > 1
          ? t('accounts.submenu.accounts_tooltip', { count: unauthorizedConsents })
          : unauthorizedConsents === 1
            ? t('accounts.submenu.accounts_tooltip_one')
            : undefined;

      accountAndCardSubsection.push({
        label: t('accounts.submenu.accounts'),
        href: RouteConfig.Accounts.buildLink(),
        notificationCount: accountsNotification,
        notificationTooltip,
      });
    }

    if (isSibillAccountEnabled) {
      accountAndCardSubsection.push({
        label: t('accounts.submenu.cards'),
        href: RouteConfig.AccountsCards.buildLink(),
      });
    }

    if (showCreditLines) {
      accountAndCardSubsection.push({
        label: t('accounts.submenu.credit_lines'),
        href: RouteConfig.AccountsCreditLines.buildLink(),
      });
    }

    const manageSubsection: SubmenuSection['items'] = [
      {
        label: t('accounts.submenu.rules'),
        href: `${RouteConfig.Transactions.buildLink()}/rules`,
        matchSubRoutes: true,
        icon: showRulesUpsellIcon ? UpsellIcon.VariantAnimated : undefined,
      },
    ];

    const result: SubmenuSection[] = [];

    if (overviewSubsection.length > 0) {
      result.push({ label: t('accounts.submenu.overview'), items: overviewSubsection });
    }

    if (!hasHideBalances || isSibillAccountEnabled || showCreditLines) {
      result.push({ label: t('accounts.submenu.accounts_and_cards'), items: accountAndCardSubsection });
    }

    if (showRules) {
      result.push({ label: t('accounts.submenu.manage'), items: manageSubsection });
    }

    return result;
  }, [
    isOnlySwan,
    hasPayments,
    hasF24Management,
    showAutomaticReconciliation,
    hasHideBalances,
    isSibillAccountEnabled,
    t,
    showRulesUpsellIcon,
    showRules,
    showCreditLines,
    timeoutTotalCount,
    showNewFeatureBadge,
    hasAutomaticReconciliation,
    unauthorizedConsents,
    accountsNotification,
  ]);

  const isActive = Boolean(useRouteMatch(sections.flatMap((s) => s.items.map((i) => i.href ?? ''))));

  return (
    <NavigationSidebarItem
      disabled={disabled}
      href={landingPage}
      iconComponent={BankIcon}
      isSubmenuOpen={isSubmenuOpen}
      onOpenSubmenu={openSubmenu}
      title={t('accounts.label')}
      notificationCount={notificationBadgeCount}
      submenuSections={hasZeroAccounts ? undefined : sections}
      isActive={isActive}
    />
  );
};

export default memo(BanksItem);
